import React, { useState } from 'react';
import { Plus, TrendingUp, X } from 'lucide-react';
import { Property } from '../types';
import { formatCurrency, calculateCapRate, calculateROI } from '../utils/helpers';
import { Link } from 'react-router-dom';
import { useProperties } from '../hooks/useProperties';

interface PropertyFormData {
  address: string;
  purchase_price: string;
  current_value: string;
  monthly_rent: string;
  annual_taxes: string;
  annual_expenses: string;
}

export const PropertiesPage: React.FC = () => {
  const { properties, addProperty } = useProperties();
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<PropertyFormData>({
    address: '',
    purchase_price: '',
    current_value: '',
    monthly_rent: '',
    annual_taxes: '',
    annual_expenses: ''
  });
  // const [marketValues, setMarketValues] = useState<{ [id: string]: number | null }>({});
  // const [loadingValues, setLoadingValues] = useState<{ [id: string]: boolean }>({});

  // useEffect(() => {
  //   properties.forEach((property: Property) => {
  //     setLoadingValues((prev) => ({ ...prev, [property.id]: true }));
  //     fetchPropertyMarketValue(property.address, property.current_value).then((value) => {
  //       setMarketValues((prev) => ({ ...prev, [property.id]: value }));
  //       setLoadingValues((prev) => ({ ...prev, [property.id]: false }));
  //     });
  //   });
  // }, [properties]);

  const handleInputChange = (field: keyof PropertyFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Create new property
    const newProperty: Property = {
      id: Date.now().toString(),
      user_id: 'user1', // Default user ID for now
      address: formData.address,
      purchase_price: parseFloat(formData.purchase_price) || 0,
      current_value: parseFloat(formData.current_value) || 0,
      monthly_rent: parseFloat(formData.monthly_rent) || 0,
      annual_taxes: parseFloat(formData.annual_taxes) || 0,
      annual_expenses: parseFloat(formData.annual_expenses) || 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    addProperty(newProperty);
    
    // Reset form
    setFormData({
      address: '',
      purchase_price: '',
      current_value: '',
      monthly_rent: '',
      annual_taxes: '',
      annual_expenses: ''
    });
    
    setShowAddForm(false);
  };

  const resetForm = () => {
    setFormData({
      address: '',
      purchase_price: '',
      current_value: '',
      monthly_rent: '',
      annual_taxes: '',
      annual_expenses: ''
    });
    setShowAddForm(false);
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-2">
        <div>
          <h1 className="text-3xl font-semibold text-neutral-900 dark:text-gray-100">Properties</h1>
          <p className="text-sm text-neutral-500 dark:text-gray-300 mt-1">Manage your real estate investments</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-neutral-900 text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-neutral-800 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-gray-200 transition"
        >
          <Plus className="w-4 h-4 mr-2 inline" /> Add Property
        </button>
      </div>

      {/* Add Property Form */}
      {showAddForm && (
        <div className="bg-white dark:bg-gray-900 border border-neutral-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-gray-100">Add New Property</h2>
            <button
              onClick={resetForm}
              className="text-neutral-400 hover:text-neutral-600 dark:hover:text-gray-200 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-gray-200 mb-2">
                Property Address *
              </label>
              <input
                type="text"
                required
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-neutral-500 focus:border-transparent transition"
                placeholder="123 Main St, City, State 12345"
              />
            </div>

            {/* Purchase Price and Current Value */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Purchase Price *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.purchase_price}
                    onChange={(e) => handleInputChange('purchase_price', e.target.value)}
                    className="w-full pl-8 pr-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-neutral-500 focus:border-transparent transition"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Current Value *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.current_value}
                    onChange={(e) => handleInputChange('current_value', e.target.value)}
                    className="w-full pl-8 pr-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-neutral-500 focus:border-transparent transition"
                    placeholder="0.00"
                  />
                </div>
                <p className="text-xs text-neutral-500 dark:text-gray-400 mt-1">Will be auto-updated from market data</p>
              </div>
            </div>

            {/* Monthly Rent and Expenses */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Monthly Rent
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.monthly_rent}
                    onChange={(e) => handleInputChange('monthly_rent', e.target.value)}
                    className="w-full pl-8 pr-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-neutral-500 focus:border-transparent transition"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Annual Taxes
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.annual_taxes}
                    onChange={(e) => handleInputChange('annual_taxes', e.target.value)}
                    className="w-full pl-8 pr-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-neutral-500 focus:border-transparent transition"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Annual Expenses
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.annual_expenses}
                    onChange={(e) => handleInputChange('annual_expenses', e.target.value)}
                    className="w-full pl-8 pr-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-neutral-500 focus:border-transparent transition"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-neutral-200">
              <button
                type="submit"
                className="flex-1 bg-neutral-900 text-white rounded-lg px-6 py-3 text-sm font-medium hover:bg-neutral-800 transition"
              >
                Add Property
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="flex-1 bg-white text-neutral-700 border border-neutral-300 rounded-lg px-6 py-3 text-sm font-medium hover:bg-neutral-50 transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Properties Grid */}
      <div className="space-y-6 md:space-y-0 md:grid md:grid-cols-3 md:gap-6">
        {properties.map((property: Property) => {
          const appreciation = property.current_value - property.purchase_price;
          const appreciationPercent = (appreciation / property.purchase_price) * 100;
          const capRate = calculateCapRate(property);
          const roi = calculateROI(property);
          return (
            <Link
              to={`/app/properties/${property.id}`}
              key={property.id}
              className="block bg-white dark:bg-gray-900 border border-neutral-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md dark:hover:shadow-lg transition-shadow flex flex-col justify-between h-full cursor-pointer"
            >
              {/* Property Info */}
              <div className="mb-4">
                <h2 className="text-lg font-medium text-neutral-800 dark:text-gray-100">{property.address}</h2>
                <div className="text-sm text-neutral-500 dark:text-gray-300 mt-1">Purchase Price: {formatCurrency(property.purchase_price)}</div>
                <div className="text-sm text-neutral-500 dark:text-gray-300">Current Value: {formatCurrency(property.current_value)}</div>
                <div className="text-xs italic text-neutral-400 dark:text-gray-400 mt-1">Auto-updated from market data</div>
              </div>
              {/* Metrics */}
              <div className="flex flex-col gap-2 mt-auto">
                <div className="inline-flex items-center gap-1 bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 rounded-md px-2 py-1 text-xs font-medium w-fit">
                  +{formatCurrency(appreciation)} ({appreciationPercent.toFixed(1)}%)
                </div>
                <div className="inline-flex items-center gap-1 bg-green-50 dark:bg-green-900/40 text-green-600 dark:text-green-300 rounded-md px-2 py-1 text-xs font-medium w-fit">
                  <TrendingUp className="w-4 h-4" /> CAP Rate: {capRate.toFixed(2)}%
                </div>
                <div className="inline-flex items-center gap-1 bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 rounded-md px-2 py-1 text-xs font-medium w-fit">
                  <span role="img" aria-label="money">💰</span> ROI: {roi.toFixed(2)}%
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}; 